import { Button } from '@oysterjs/ui/button';

import { AmsRefusalKind } from '@oysterjs/ais/api/graphql';
import type { DestinationOutcome, RefusedRecord } from './review-changes';

/**
 * Step four: what happened at each destination. Counts come from the apply results,
 * and refused or unanswered destinations are listed with the rest, so a partial
 * failure never reads as a clean write.
 */
export function ApplyReceipt(props: {
  accountName: string;
  outcomes: DestinationOutcome[];
  onRetry?: () => void;
  onDone: () => void;
}) {
  const total = props.outcomes.reduce(
    (sum, outcome) =>
      sum + (outcome.status === 'written' || outcome.status === 'partial' ? outcome.written : 0),
    0
  );
  const unwritten = props.outcomes.filter((outcome) => outcome.status !== 'written').length;

  return (
    <div>
      <p className="m-0 text-lg/6 font-semibold text-neutral-950 dark:text-white">
        {total} {total === 1 ? 'change' : 'changes'} written to AMS
      </p>
      <p className="m-0 mt-2 text-sm/6 text-neutral-500 dark:text-neutral-400">
        {props.accountName} ·{' '}
        {new Date().toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })}
        {unwritten > 0 &&
          ` · ${unwritten} ${unwritten === 1 ? 'destination needs' : 'destinations need'} attention`}
      </p>

      <div className="mt-6 overflow-hidden rounded-lg border border-neutral-950/10 dark:border-white/10">
        {props.outcomes.map((outcome) => (
          <div
            key={outcome.targetId}
            className="flex flex-col gap-1 border-b border-neutral-950/5 px-3.5 py-3 last:border-b-0 dark:border-white/5"
          >
            <div className="flex items-center gap-3">
              <span className="min-w-0 break-words text-sm/5 font-medium text-neutral-950 dark:text-white">
                {outcome.label}
              </span>
              <OutcomeSummary outcome={outcome} />
            </div>
            {outcome.status === 'refused' && (
              <p className="m-0 text-xs text-red-700 dark:text-red-300">{outcome.message}</p>
            )}
            {outcome.status === 'partial' &&
              outcome.refused.map((record) => (
                <p
                  key={record.key}
                  className={
                    record.kind === AmsRefusalKind.Unconfirmed
                      ? 'm-0 text-xs text-amber-700 dark:text-amber-300'
                      : 'm-0 text-xs text-red-700 dark:text-red-300'
                  }
                >
                  {refusalLine(record)}
                </p>
              ))}
            {outcome.status === 'unconfirmed' && (
              <p className="m-0 text-xs text-amber-700 dark:text-amber-300">
                The AMS didn't answer, so this may or may not have been written. Check Applied Epic
                before relying on it; sending the same values again is safe.
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-end gap-3">
        {props.onRetry && unwritten > 0 && (
          <Button type="button" color="white" onClick={props.onRetry}>
            Review unwritten changes
          </Button>
        )}
        <Button type="button" color="sky" onClick={props.onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

function OutcomeSummary(props: { outcome: DestinationOutcome }) {
  const { outcome } = props;
  switch (outcome.status) {
    case 'written':
      return (
        <span className="ml-auto shrink-0 text-sm/5 text-neutral-500 dark:text-neutral-400">
          {outcome.written} {outcome.written === 1 ? 'change' : 'changes'}
        </span>
      );
    case 'refused':
      return (
        <span className="ml-auto shrink-0 text-sm/5 font-medium text-red-700 dark:text-red-300">
          Not written
        </span>
      );
    case 'partial': {
      // Any kind but UNCONFIRMED, known or not, reads as not written, as its line does.
      const count = (unconfirmed: boolean) =>
        outcome.refused
          .filter((record) => (record.kind === AmsRefusalKind.Unconfirmed) === unconfirmed)
          .reduce((sum, record) => sum + record.fields.length, 0);
      const [notWritten, unconfirmed] = [count(false), count(true)];
      return (
        <span
          className={
            notWritten > 0
              ? 'ml-auto shrink-0 text-sm/5 font-medium text-red-700 dark:text-red-300'
              : 'ml-auto shrink-0 text-sm/5 font-medium text-amber-700 dark:text-amber-300'
          }
        >
          {outcome.written} written
          {notWritten > 0 && ` · ${notWritten} not written`}
          {unconfirmed > 0 && ` · ${unconfirmed} couldn't confirm`}
        </span>
      );
    }
    case 'unconfirmed':
      return (
        <span className="ml-auto shrink-0 text-sm/5 font-medium text-amber-700 dark:text-amber-300">
          Couldn't confirm
        </span>
      );
  }
}

// Only the AMS's own refusal is quoted as the AMS's words; a change Cara never sent,
// or sent as the request ended, says so. A kind this bundle predates reads as not
// written, which claims nothing about the AMS.
function refusalLine(record: RefusedRecord): string {
  const fields = fieldList(record.fields);
  switch (record.kind) {
    case AmsRefusalKind.RefusedByAms:
      return `${record.subject} (${fields}) not written. Applied Epic said: ${record.message}`;
    case AmsRefusalKind.NotWritten:
      return `${record.subject} (${fields}) not written: ${record.message}`;
    case AmsRefusalKind.Unconfirmed:
      return `${record.subject} (${fields}) may or may not have been written: ${record.message}. Check Applied Epic; sending the same values again is safe.`;
    default: {
      const _unknown: never = record.kind;
      void _unknown;
      return `${record.subject} (${fields}) not written`;
    }
  }
}

// "Limit, Valuation and Blanket number", or the first three and how many more.
export function fieldList(fields: readonly string[]): string {
  const shown = fields.length > 3 ? [...fields.slice(0, 3), `${fields.length - 3} more`] : fields;
  return shown.length > 1
    ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
    : (shown[0] ?? '');
}
